import { useState } from "react";
import { motion } from "framer-motion";
import { Mail, MapPin, Phone, Send, Instagram, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";

export default function ContattiPage() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: ""
  });
  const [sending, setSending] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.message) {
      toast.error("Compila tutti i campi obbligatori");
      return;
    }

    setSending(true);
    
    // Simulate sending (in production, this would send to a backend)
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    toast.success("Messaggio inviato!", {
      description: "Ti risponderemo il prima possibile."
    });
    
    setFormData({ name: "", email: "", subject: "", message: "" });
    setSending(false);
  };

  return (
    <div className="min-h-screen bg-white" data-testid="contatti-page">
      {/* Hero */}
      <section className="bg-accent-blue py-16 border-b-4 border-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl"
          >
            <h1 className="font-heading text-6xl sm:text-7xl mb-4">
              CONTATTI
            </h1>
            <p className="font-body text-xl text-gray-800">
              Hai domande? Vuoi collaborare? Scrivi pure, 
              siamo sempre felici di sentirti!
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Contact Info */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
            >
              <h2 className="font-heading text-4xl mb-8">PARLIAMONE</h2>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                {/* Email */}
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 bg-accent-pink border-2 border-black flex items-center justify-center flex-shrink-0">
                    <Mail size={24} className="text-white" />
                  </div>
                  <div>
                    <h3 className="font-heading text-xl mb-1">EMAIL</h3>
                    <a 
                      href="mailto:info@rikami.it" 
                      className="font-body text-lg hover:text-accent-pink"
                    >
                      info@rikami.it
                    </a>
                    <p className="font-body text-sm text-gray-500 mt-1">
                      Rispondiamo entro 24 ore
                    </p>
                  </div>
                </div>

                {/* Phone */}
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 bg-accent-yellow border-2 border-black flex items-center justify-center flex-shrink-0">
                    <Phone size={24} />
                  </div>
                  <div>
                    <h3 className="font-heading text-xl mb-1">TELEFONO</h3>
                    <a 
                      href="tel:+39 3911246837" 
                      className="font-body text-lg hover:text-accent-pink"
                    >
                      +39 391 124 68 37
                    </a>
                    <p className="font-body text-sm text-gray-500 mt-1">
                      Lun-Ven, 9:00 - 18:00
                    </p>
                  </div>
                </div>

                {/* Address */}
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 bg-accent-blue border-2 border-black flex items-center justify-center flex-shrink-0">
                    <MapPin size={24} />
                  </div>
                  <div>
                    <h3 className="font-heading text-xl mb-1">SEDE</h3>
                    <p className="font-body text-lg">
                      Via del Design 42<br />
                      10040 La Loggia, Italia
                    </p>
                  </div>
                </div>

                {/* Social */}
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 bg-accent-green border-2 border-black flex items-center justify-center flex-shrink-0">
                    <MessageCircle size={24} />
                  </div>
                  <div>
                    <h3 className="font-heading text-xl mb-1">SOCIAL</h3>
                    <div className="flex gap-3 mt-2">
                      <a 
                        href="https://instagram.com/rikamibrand"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-3 border-2 border-black hover:bg-accent-pink hover:text-white"
                      >
                        <Instagram size={20} />
                      </a>
                    </div>
                  </div>
                </div>
              </div>

              {/* FAQ */}
              <div className="mt-12 p-6 border-4 border-black bg-gray-50">
                <h3 className="font-heading text-2xl mb-4">DOMANDE FREQUENTI</h3>
                <div className="space-y-4">
                  <div>
                    <h4 className="font-body font-bold">Quanto tempo per la spedizione?</h4>
                    <p className="font-body text-sm text-gray-600">
                      3-5 giorni lavorativi in Italia
                    </p>
                  </div>
                  <div>
                    <h4 className="font-body font-bold">Posso fare un reso?</h4>
                    <p className="font-body text-sm text-gray-600">
                      Sì, entro 14 giorni dall'acquisto
                    </p>
                  </div>
                  <div>
                    <h4 className="font-body font-bold">Fate personalizzazioni?</h4>
                    <p className="font-body text-sm text-gray-600">
                      Contattaci per richieste speciali
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
            >
              <div className="border-4 border-black p-8">
                <h2 className="font-heading text-3xl mb-6">SCRIVICI</h2>
                
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <Label htmlFor="name" className="font-mono text-xs uppercase">
                      NOME *
                    </Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      className="border-2 border-black rounded-none mt-2"
                      required
                      data-testid="contact-name"
                    />
                  </div>

                  <div>
                    <Label htmlFor="email" className="font-mono text-xs uppercase">
                      EMAIL *
                    </Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      className="border-2 border-black rounded-none mt-2"
                      required
                      data-testid="contact-email"
                    />
                  </div>

                  <div>
                    <Label htmlFor="subject" className="font-mono text-xs uppercase">
                      OGGETTO
                    </Label>
                    <Input
                      id="subject"
                      value={formData.subject}
                      onChange={(e) => setFormData({...formData, subject: e.target.value})}
                      className="border-2 border-black rounded-none mt-2"
                      data-testid="contact-subject"
                    />
                  </div>

                  <div>
                    <Label htmlFor="message" className="font-mono text-xs uppercase">
                      MESSAGGIO *
                    </Label>
                    <Textarea
                      id="message"
                      value={formData.message}
                      onChange={(e) => setFormData({...formData, message: e.target.value})}
                      className="border-2 border-black rounded-none mt-2 min-h-[150px]"
                      required
                      data-testid="contact-message"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={sending}
                    className={`
                      w-full py-4 font-heading text-xl border-4 border-black
                      flex items-center justify-center gap-2
                      ${sending 
                        ? 'bg-gray-200 cursor-not-allowed' 
                        : 'bg-black text-white hover:bg-accent-pink hover:shadow-brutal'
                      }
                    `}
                    data-testid="contact-submit"
                  >
                    {sending ? (
                      <div className="w-6 h-6 border-2 border-white border-t-transparent animate-spin"></div>
                    ) : (
                      <>
                        <Send size={20} />
                        INVIA MESSAGGIO
                      </>
                    )}
                  </button>
                </form>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
}
